// ============================================================
// APP SHELL — react-router-dom v6 routing + アプリシェル。
// <AuthProvider> でラップし、<Sidebar/> + <TopBar/> + <Outlet/> を組む。
// <768px はサイドバーを off-canvas ドロワーにし、ハンバーガーで開閉する
// （drawerOpen state を Sidebar / TopBar に渡す）。
// main.tsx は default export の <App/> を期待する。
// ============================================================
import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
// apple-design 第3段: LazyMotion(domMax)で必要機能だけ読み込み・strict で motion.* の誤用をビルド時排除。
// MotionConfig reducedMotion="user" = OS の「視差効果を減らす」で transform 系アニメを自動無効化（opacity は残る）。
import { LazyMotion, domMax, MotionConfig, m } from 'motion/react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { GoogleDataProvider } from './lib/GoogleDataContext';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import OnboardingTour from './components/OnboardingTour';
import ErrorBoundary from './components/ErrorBoundary';
import { hasOnboarded } from './lib/onboarding';
import { lazyWithRetry } from './lib/lazyRetry';
// Home（初期表示）と Login（認証ゲート直下）は初回描画に必要なので同期 import のまま。
// Privacy は公開ページ（未ログインの Google 審査官が直接開く）なので、
// Suspense（AppShell 内）を経由しない同期 import にする（静的・軽量）。
import Home from './screens/Home';
import Login from './screens/Login';
import Privacy from './screens/Privacy';
// それ以外の画面はルート単位で遅延読み込み（初回ロードの JS を軽くする）。
// chunk 読み込み失敗（新デプロイ後の古 chunk 404 等）は lazyWithRetry が1回だけリロードで回復。
const NotesList = lazyWithRetry(() => import('./screens/NotesList'));
const NoteDetail = lazyWithRetry(() => import('./screens/NoteDetail'));
const Knowledge = lazyWithRetry(() => import('./screens/Knowledge'));
const MyTasks = lazyWithRetry(() => import('./screens/MyTasks'));
const Calendar = lazyWithRetry(() => import('./screens/Calendar'));
const Projects = lazyWithRetry(() => import('./screens/Projects'));
const OneOnOne = lazyWithRetry(() => import('./screens/OneOnOne'));
const AxExec = lazyWithRetry(() => import('./screens/AxExec'));
const AxReport = lazyWithRetry(() => import('./screens/AxReport'));
const DriveActivity = lazyWithRetry(() => import('./screens/DriveActivity'));
const TeamTasks = lazyWithRetry(() => import('./screens/TeamTasks'));
const Settings = lazyWithRetry(() => import('./screens/Settings'));
const Assistant = lazyWithRetry(() => import('./screens/Assistant'));
const Upload = lazyWithRetry(() => import('./screens/Upload'));
const Search = lazyWithRetry(() => import('./screens/Search'));

/** シェル — サイドバー + トップバー + ルートのアウトレット。 */
function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  // 既読フラグは Auth uid（ユーザー単位）でひく。viewer.id（安定 member id）ではなく
  // uid を使うことで、端末をまたいでも「1人につき1回だけ」自動表示される。
  const { uid } = useAuth();
  // 初回ログイン時のみオンボーディングガイドを自動表示（uid 単位の既読フラグ）。
  // 「ガイド」導線（TopBar）から手動再表示するときは既読でも開く。
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    // uid 確定後に初回判定（Firestore 優先・非同期）。既読なら何もしない（自動表示は1回だけ）。
    // uid 無し（未ログイン）では出さない。古い結果での上書きを防ぐため cancelled ガード。
    if (!uid) return;
    let cancelled = false;
    void hasOnboarded(uid).then((done) => {
      if (!cancelled && !done) setGuideOpen(true);
    });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  return (
    // bg は透明にして body::before のアンビエント背景（index.css LAYER 2）を透かす
    <div className="min-h-screen flex bg-transparent text-charcoal">
      <Sidebar drawerOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar onOpenDrawer={() => setDrawerOpen(true)} onOpenGuide={() => setGuideOpen(true)} />
        <main className="flex-1 min-w-0 px-4 py-5 sm:px-6 lg:px-8 max-w-[1200px] w-full mx-auto">
          {/* 画面単位の ErrorBoundary：lazy chunk の最終失敗（lazyRetry のリロード後も
              失敗するケース）や画面内の描画エラーを、シェルを残したままここで受ける。 */}
          <ErrorBoundary>
            {/* 画面遷移: パスが変わるたびに臨界減衰スプリングで浮き上がる（enterのみ・exit待ちなし） */}
            <m.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
            >
              {/* lazy 画面の読み込み待ちフォールバック（既存の AuthGate ローディングと同トーン） */}
              <Suspense
                fallback={
                  <div className="py-16 text-center text-slate-sub text-sm">読み込み中…</div>
                }
              >
                <Outlet />
              </Suspense>
            </m.div>
          </ErrorBoundary>
        </main>
      </div>
      {guideOpen && <OnboardingTour onClose={() => setGuideOpen(false)} />}
    </div>
  );
}

/** ルーティングされたアプリ本体（viewer 確定後にのみ描画される）。
 *  BrowserRouter は App() 最上位に移動済み（/privacy を AuthGate の外に置くため）。
 *  ここは App() の `/*` 配下にネストされた descendant Routes として動く。 */
function RoutedApp() {
  return (
    <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Home />} />
          <Route path="notes" element={<NotesList />} />
          <Route path="notes/:id" element={<NoteDetail />} />
          <Route path="upload" element={<Upload />} />
          <Route path="assistant" element={<Assistant />} />
          <Route path="search" element={<Search />} />
          <Route path="tasks" element={<MyTasks />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="projects" element={<Projects />} />
          <Route path="oneonone" element={<OneOnOne />} />
          <Route path="ax" element={<AxExec />} />
          <Route path="ax-report" element={<AxReport />} />
          <Route path="drive-activity" element={<DriveActivity />} />
          <Route path="knowledge" element={<Knowledge />} />
          {/* 旧5画面のパスは /knowledge へ集約（2026-07-02 IA再編・古いリンクを壊さない） */}
          <Route path="announcements" element={<Navigate to="/knowledge" replace />} />
          <Route path="axcases" element={<Navigate to="/knowledge" replace />} />
          <Route path="prompts" element={<Navigate to="/knowledge" replace />} />
          <Route path="apps" element={<Navigate to="/knowledge" replace />} />
          <Route path="members" element={<TeamTasks />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
  );
}

/**
 * 認証ゲート。
 *   - DEV モード: viewer は常に確定 → そのままアプリ。
 *   - LIVE モード: loading 中はスピナー、未ログイン（viewer=null）は <Login/>、
 *     ログイン確定後にアプリを描画する。
 * ゲートを通過した時点で viewer は必ず非null（useViewer が安全に使える）。
 */
function AuthGate() {
  const { viewer, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-base text-slate-sub text-sm">
        読み込み中…
      </div>
    );
  }
  if (!viewer) return <Login />;
  // GoogleDataProvider は AuthGate の内側（ログイン後のみ）に置く：
  // トークン確定時のブートストラップ同期＋Google予定の共有ストア（Home/Calendar が消費）。
  return (
    <GoogleDataProvider>
      <RoutedApp />
    </GoogleDataProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LazyMotion features={domMax} strict>
        <MotionConfig reducedMotion="user">
          <BrowserRouter>
            <Routes>
              {/* 公開ページ（ログイン不要）— Google OAuth 審査官が未ログインで開く。 */}
              <Route path="/privacy" element={<Privacy />} />
              {/* それ以外は認証ゲート配下（未ログインは Login、確定後は RoutedApp）。 */}
              <Route path="/*" element={<AuthGate />} />
            </Routes>
          </BrowserRouter>
        </MotionConfig>
      </LazyMotion>
    </AuthProvider>
  );
}
